import { Bar, Cyl, Slab } from '../../render/parts'
import type { AssetContext, AssetDefinition } from '../types'

/**
 * Standing / cafeteria high table: 1.05 m top on four tubular legs, braced by a
 * perimeter foot rail and a pair of long under-top rails.
 *
 * Footprint is declared at the 1.6 m variant; the 1.2 m top is centred in it.
 */

const DEPTH = 0.8
const HEIGHT = 1.05
const TOP = 0.036
const UNDER = HEIGHT - TOP

const WIDTH_W160 = 1.6
const WIDTH_W120 = 1.2

const WIDTHS: Record<string, number> = {
  w160: WIDTH_W160,
  w120: WIDTH_W120,
}

const LEG_R = 0.03
/** Foot rail height — a comfortable rest for someone standing at the table. */
const RAIL_Y = 0.2

function TableHigh({ variant = 'w160' }: AssetContext) {
  const width = WIDTHS[variant] ?? WIDTH_W160
  const x = width / 2 - 0.1
  const z = DEPTH / 2 - 0.1
  /** Legs start inside the floor collar and end 6 mm up inside the top. */
  const legBase = 0.018
  const legTop = UNDER + 0.006

  const corners = [
    [-x, -z],
    [x, -z],
    [-x, z],
    [x, z],
  ] as const

  return (
    <group>
      <Slab tone="surface" size={[width, DEPTH, TOP]} radius={0.022} position={[0, HEIGHT - TOP / 2, 0]} />

      {corners.map(([lx, lz]) => (
        <group key={`${lx},${lz}`}>
          <Cyl
            tone="metal"
            r={LEG_R}
            h={legTop - legBase}
            segments={18}
            position={[lx, (legTop + legBase) / 2, lz]}
          />
          {/* Floor collar swallows the leg's bottom cap. */}
          <Cyl tone="darkDeep" r={0.04} h={0.03} segments={18} position={[lx, 0.015, lz]} />
        </group>
      ))}

      {/* Perimeter foot rail. */}
      {[-1, 1].map((side) => (
        <Bar
          key={`long-${side}`}
          tone="metal"
          r={0.018}
          from={[-x, RAIL_Y, side * z]}
          to={[x, RAIL_Y, side * z]}
        />
      ))}
      {[-1, 1].map((side) => (
        <Bar
          key={`short-${side}`}
          tone="metal"
          r={0.018}
          from={[side * x, RAIL_Y, -z]}
          to={[side * x, RAIL_Y, z]}
        />
      ))}

      {/* Under-top rails, tucked just below the worktop. */}
      {[-1, 1].map((side) => (
        <Bar
          key={`apron-${side}`}
          tone="metal"
          r={0.02}
          from={[-x, UNDER - 0.05, side * z]}
          to={[x, UNDER - 0.05, side * z]}
        />
      ))}
    </group>
  )
}

export const definition: AssetDefinition = {
  id: 'table-high',
  name: 'Mesa alta',
  category: 'tables',
  footprint: [WIDTH_W160, DEPTH],
  height: HEIGHT,
  surfaceHeight: HEIGHT,
  seats: 4,
  occupantSide: 'around',
  variants: [
    { id: 'w160', name: '1.6 m', footprint: [WIDTH_W160, DEPTH] },
    { id: 'w120', name: '1.2 m', footprint: [WIDTH_W120, DEPTH] },
  ],
  keywords: [
    'mesa',
    'alta',
    'bar',
    'cafetería',
    'de pie',
    'high table',
    'standing table',
    'counter',
  ],
  Component: TableHigh,
}
